/* eslint-disable @typescript-eslint/naming-convention */
import * as OldApiService from 'config/apiServiceConfig'
import ApiService from 'config/apiServiceApiGateway'
import type {
  BarcodeInputMethodType,
  PurchaseOrderInboundDetailsInterface,
  PurchaseOrderInboundItemsInterface,
  PurchaseOrderInboundItemVerificationResultInterface,
  ManualReasonType,
  PurchaseOrderInboundItemsQueryParamsInterface,
  PurchaseOrderInboundItemSummaryInterface,
} from '../@types/CheckQuantity'

export interface PostPurchaseOrderInboundItemVerificationRequestInterface {
  sku_number: string
  input_method: BarcodeInputMethodType
  manual_reason: Nullable<number>
  checker_ids?: number[]
}

export interface PostPurchaseOrderInboundItemRequestInterface {
  quantity: number
  status_id: number
  status_notes_id: Nullable<number>
  expiry_date: Nullable<string | number>
  package_id?: string
  product_id: number
}

export interface PostPurchaseOrderGenerateProductDetailRequestInterface {
  productId: number
  locationId: number
  stock: number
  expiryDate: number
  isActive: boolean
}
export interface PostPurchaseOrderGenerateProductDetailResponseInterface {
  data: {
    product_detail_id: number
    product_id: number
    product_name: string
    product_sku_number: string
    product_relabeled_in_hub: boolean
    location_id: number
    qr_code: string
    stock: number
    quantity: number
    expiry_date: number
    package_id_enable: boolean
    updated_expiry_date: boolean
    required_expiry_date: boolean
    enable_transfer: boolean
    rack_editable: boolean
    is_active: boolean
    produced_by?: Nullable<string>
  }
}

export interface SubmitPurchaseOrderInboundCompletePayloadInterface {
  items: {
    item_id: number
    reason_id: number
  }[]
}

export interface ApiResponseMessageOnlyInterface {
  message: string
}

export type GetManualScanReasonsRequestParamsType = {
  'pagination.page_index': number
  'pagination.page_size': number
  'filter.type'?: ManualReasonType['type']
  'filter.deleted'?: boolean
}

export const getManualScanReasonsApi = (params: GetManualScanReasonsRequestParamsType) =>
  ApiService.Get<ManualReasonType[]>({
    url: '/wims/internal/v1/reasons',
    params,
  })

export const getPurchaseOrderInboundDetailsApi = (purchaseOrderId: number) =>
  ApiService.Get<PurchaseOrderInboundDetailsInterface>({
    url: '/wims/internal/v1/purchase-order-inbounds/:purchaseOrderId',
    urlParams: { purchaseOrderId },
  })

export const getPurchaseOrderInboundItemsApi = (
  purchaseOrderId: number,
  params: PurchaseOrderInboundItemsQueryParamsInterface,
) =>
  ApiService.Get<PurchaseOrderInboundItemsInterface[]>({
    url: '/wims/internal/v1/purchase-order-inbounds/:purchaseOrderId/items',
    urlParams: { purchaseOrderId },
    params,
  })

export const getPurchaseOrderInboundItemsSummaryApi = (purchaseOrderId: number) =>
  ApiService.Get<PurchaseOrderInboundItemSummaryInterface[]>({
    url: '/wims/internal/v1/purchase-order-inbounds/:purchaseOrderId/items-summary',
    urlParams: { purchaseOrderId },
  })

export const postPurchaseOrderInboundItemVerificationApi = (
  purchaseOrderId: number,
  payload: PostPurchaseOrderInboundItemVerificationRequestInterface,
) =>
  ApiService.Post<PurchaseOrderInboundItemVerificationResultInterface>({
    url: '/wims/internal/v1/purchase-order-inbounds/:purchaseOrderId/item-products',
    urlParams: { purchaseOrderId },
    data: payload,
  })

export const postPurchaseOrderInboundItemNonAssetApi = (
  purchaseOrderId: number,
  itemId: number,
  payload: PostPurchaseOrderInboundItemRequestInterface,
) =>
  ApiService.Post<ApiResponseMessageOnlyInterface, true>({
    url: '/wims/internal/v1/purchase-order-inbounds/:purchaseOrderId/items/:itemId',
    urlParams: { purchaseOrderId, itemId },
    data: payload,
  })

export const postPurchaseOrderInboundItemAssetApi = (
  purchaseOrderId: number,
  itemId: number,
  payload: PostPurchaseOrderInboundItemRequestInterface,
) =>
  ApiService.Post<ApiResponseMessageOnlyInterface, true>({
    url: '/wims/internal/v1/purchase-orders/:purchaseOrderId/item/:itemId',
    urlParams: { purchaseOrderId, itemId },
    data: payload,
  })

export const deletePurchaseOrderInboundItemApi = (
  purchaseOrderId: number,
  inboundItemDetailId: number,
) =>
  ApiService.Delete<
    {
      message: string
      error: { status: boolean; message: string; code: number }
    },
    true
  >({
    url: '/wims/internal/v1/purchase-order-inbounds/:purchaseOrderId/item-details/:inboundItemDetailId',
    urlParams: { purchaseOrderId, inboundItemDetailId },
  })

export const patchRackInboundItemDetailApi = (
  purchaseOrderInboundItemDetailId: number,
  data: { rack_id: number },
) =>
  ApiService.Patch<ApiResponseMessageOnlyInterface, true>({
    url: '/wims/internal/v1/purchase-order-inbound-item-details/:purchaseOrderInboundItemDetailId',
    urlParams: { purchaseOrderInboundItemDetailId },
    data,
  })

export const postPurchaseOrderGenerateProductDetailApi = (
  payload: PostPurchaseOrderGenerateProductDetailRequestInterface,
) =>
  OldApiService.Post<PostPurchaseOrderGenerateProductDetailResponseInterface>({
    url: '/api/product-detail/generate',
    data: payload,
  })

export const putSubmitPurchaseOrderInboundAssetCompleteApi = (
  purchaseOrderId: number,
  payload: SubmitPurchaseOrderInboundCompletePayloadInterface,
) =>
  ApiService.Put({
    url: '/wims/internal/v1/purchase-orders/inbound/:purchaseOrderId',
    data: payload,
    urlParams: { purchaseOrderId },
    config: {
      timeout: 90000,
    },
  })

export const patchSubmitPurchaseOrderInboundNonAssetCompleteApi = (
  purchaseOrderId: number,
  payload: SubmitPurchaseOrderInboundCompletePayloadInterface,
) =>
  ApiService.Patch({
    url: '/wims/internal/v1/purchase-order-inbounds/:purchaseOrderId',
    data: payload,
    urlParams: { purchaseOrderId },
    config: {
      timeout: 90000,
    },
  })
